/**
 * spectrumNormalMap — the water DETAIL normal map from an ocean spectrum
 * (R2 of the 2026-09-25 water-realism wave).
 *
 * The old detail map (`WaterSystem.generateWaterNormalMap`) summed four
 * integer-wavenumber sines: a low-entropy periodic interference pattern that
 * reads as the classic "CG water" diamond lattice at the repeat pitch. Real
 * capillary/gravity chop is a broadband random field (Tessendorf 2001;
 * Horvath 2015). This bake builds a PERIODIC height field on the CPU from the
 * engine's own `fft-pure` spectrum (JONSWAP by default, Hermitian h0, seeded)
 * over a small patch (~8 m), takes its slopes spectrally
 * (∂h/∂x = IFFT(i·kx·h̃), ∂h/∂z = IFFT(i·kz·h̃) — exact, no finite-difference
 * blur), and packs normals the same way the old map did:
 *
 *   rgb = normalize(−sx·g, 1, −sz·g) · 0.5 + 0.5
 *
 * with g chosen so the map's RMS slope hits `targetRmsSlope` — the material's
 * detail weight stays calibrated whatever the spectrum. Periodic by
 * construction (an FFT field wraps), so RepeatWrapping never draws a seam.
 * Both slope fields ride ONE complex 2-D IFFT (sx + i·sz: each is Hermitian,
 * so the real and imaginary outputs are exactly the two real fields).
 *
 * Cost: 256² ≈ 20–40 ms once per session (512² ≈ 4×).
 */

import * as THREE from 'three/webgpu';
import { spectralDensity2D, type OceanSpectrumConfig } from './fft/fft-pure.js';

const smooth01 = (t: number): number => { const c = t < 0 ? 0 : t > 1 ? 1 : t; return c * c * (3 - 2 * c); };

export interface SpectrumNormalMapConfig extends OceanSpectrumConfig {
  /** Texels per side (power of two). Default 256. */
  resolution?: number;
  /** World size of the patch the texture represents (m). Default 8. */
  patchSize?: number;
  /** RMS slope of the stored normals (the look's energy). Default 0.3. */
  targetRmsSlope?: number;
  /** Seed. Default 1789. */
  seed?: number;
  /** Longest wavelength kept (m) — the wave set carries everything longer. Default patchSize / 3. */
  maxWavelength?: number;
  /** Also measure the mode statistics (a second FFT; harnesses). Default false. */
  fullStats?: boolean;
}

export interface SpectrumNormalStats {
  resolution: number;
  patchSize: number;
  /** RMS of the stored slope (both axes summed, i.e. √mean(sx² + sz²)). */
  rmsSlope: number;
  /** Mean-square slope of the stored field (the variance the material folds into roughness). */
  meanSquareSlope: number;
  /** Largest single Fourier mode's share of the slope variance (a lattice has ~0.25+; a spectrum ≪ 0.05). */
  peakModeShare: number;
  /** Share carried by the 4 strongest modes (the old 4-sine map: 1.0). */
  top4Share: number;
  /** Largest per-texel step across the wrap boundary vs inside (seamless ⇒ comparable). */
  maxWrapStep: number;
  maxInteriorStep: number;
  /** Bake time (ms). */
  ms: number;
}

export interface SpectrumNormalMap {
  texture: THREE.DataTexture;
  stats: SpectrumNormalStats;
}

/** Named presets: the open sea's wind chop and a sheltered lake's ripples. */
export const SPECTRUM_PRESETS: Record<'choppy' | 'calm', SpectrumNormalMapConfig> = {
  choppy: { model: 'jonswap', windSpeed: 6, fetchKm: 100, windDirectionDeg: 25, patchSize: 8, targetRmsSlope: 0.6, seed: 4177 },
  calm: { model: 'jonswap', windSpeed: 3, fetchKm: 2, windDirectionDeg: 25, patchSize: 5, targetRmsSlope: 0.35, seed: 911 },
};

/** In-place iterative radix-2 complex FFT (e^{+iθ} kernel = inverse, unnormalised). */
function fftInPlace(re: Float64Array, im: Float64Array, n: number, cosT: Float64Array, sinT: Float64Array): void {
  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      let t = re[i]; re[i] = re[j]; re[j] = t;
      t = im[i]; im[i] = im[j]; im[j] = t;
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const half = len >> 1;
    const step = n / len;
    for (let i = 0; i < n; i += len) {
      for (let k = 0; k < half; k++) {
        const wr = cosT[k * step], wi = sinT[k * step];
        const a = i + k, b = a + half;
        const xr = re[b] * wr - im[b] * wi;
        const xi = re[b] * wi + im[b] * wr;
        re[b] = re[a] - xr; im[b] = im[a] - xi;
        re[a] += xr; im[a] += xi;
      }
    }
  }
}

/**
 * The periodic slope fields (sx, sz) of a spectrum height field, N×N,
 * row-major (index = z·N + x), metres per metre. Pure — no three.js use.
 */
export function spectrumSlopeField(config: SpectrumNormalMapConfig = {}): { sx: Float64Array; sz: Float64Array; N: number; patchSize: number } {
  const N = 2 ** Math.round(Math.log2(Math.min(1024, Math.max(16, config.resolution ?? 256))));
  const L = Math.max(0.5, config.patchSize ?? 8);
  const dk = (Math.PI * 2) / L;
  // THE HIGH-PASS: the wave set (Gerstner or FFT cascades) carries every wavelength
  // longer than a few metres per pixel; the detail map is the chop BELOW that.
  // Without it the patch's own fundamental ring (λ = L) holds most of the slope
  // energy of a k⁻⁴ tail and repeats visibly at the tile pitch.
  const kc = (Math.PI * 2) / Math.max(1e-3, config.maxWavelength ?? L / 3);
  // At t = 0 the Tessendorf field is h̃(k) = h0(k) + conj(h0(−k)): statistically a
  // Hermitian field with power S(k) + S(−k). Drawn DIRECTLY as Hermitian pairs —
  // half the density evaluations of generateH0 and a real field by construction
  // (the renormalisation below makes absolute amplitude irrelevant).
  const radial = new Float64Array((N * N) / 2 + 1).fill(-1);
  const windRad = ((config.windDirectionDeg ?? 25) * Math.PI) / 180;
  const wcx = Math.cos(windRad), wcz = Math.sin(windRad);
  const radialOf = (n2: number): number => {
    let v = radial[n2];
    if (v >= 0) return v;
    const k = Math.sqrt(n2) * dk;
    // Direction-free part: spectralDensity2D with the wind along k (dot = 1) is S·1.
    v = spectralDensity2D(k * wcx, k * wcz, config);
    radial[n2] = v;
    return v;
  };
  const dirOf = (kx: number, kz: number, k: number): number => {
    const d = (kx * wcx + kz * wcz) / k;
    return d > 0 ? d * d : d * d * 0.15; // fft-pure's cos² spread, back-travelling half suppressed
  };
  let lcgState = (config.seed ?? 1789) >>> 0;
  const rnd = (): number => { lcgState = (lcgState * 1664525 + 1013904223) >>> 0; return lcgState / 0xffffffff; };
  const re = new Float64Array(N * N), im = new Float64Array(N * N);
  for (let y = 0; y < N; y++) {
    const yi = y - N / 2;
    for (let x = 0; x < N; x++) {
      const i = y * N + x;
      const mx = (N - x) % N, my = (N - y) % N;
      const j = my * N + mx;
      if (j < i) continue; // the partner was drawn with this pair
      const xi = x - N / 2;
      if (xi === 0 && yi === 0) continue; // no mean slope
      const kx = xi * dk, kz = yi * dk;
      const k = Math.hypot(kx, kz);
      const hp = smooth01((k - kc * 0.5) / (kc * 0.5));
      const p = radialOf(xi * xi + yi * yi) * (dirOf(kx, kz, k) + dirOf(-kx, -kz, k));
      const amp = Math.sqrt(Math.max(0, p)) * hp;
      // Box–Muller complex gaussian.
      const u1 = Math.max(1e-12, rnd()), u2 = rnd();
      const r = Math.sqrt(-2 * Math.log(u1));
      const hr = r * Math.cos(Math.PI * 2 * u2) * amp;
      const hi = j === i ? 0 : r * Math.sin(Math.PI * 2 * u2) * amp;
      // i·kx·h̃ = (−kx·hi, kx·hr); i·kz·h̃ = (−kz·hi, kz·hr); F = Sx + i·Sz.
      re[i] = -kx * hi - kz * hr;
      im[i] = kx * hr - kz * hi;
      if (j !== i) {
        // Partner −k carries conj(h̃): (hr, −hi) at wavevector (−kx, −kz).
        const pkx = -kx, pkz = -kz, phr = hr, phi = -hi;
        re[j] = -pkx * phi - pkz * phr;
        im[j] = pkx * phr - pkz * phi;
      }
    }
  }
  const cosT = new Float64Array(N / 2), sinT = new Float64Array(N / 2);
  for (let k = 0; k < N / 2; k++) { cosT[k] = Math.cos((Math.PI * 2 * k) / N); sinT[k] = Math.sin((Math.PI * 2 * k) / N); }
  const rr = new Float64Array(N), ri = new Float64Array(N);
  // Rows.
  for (let y = 0; y < N; y++) {
    const b = y * N;
    for (let x = 0; x < N; x++) { rr[x] = re[b + x]; ri[x] = im[b + x]; }
    fftInPlace(rr, ri, N, cosT, sinT);
    for (let x = 0; x < N; x++) { re[b + x] = rr[x]; im[b + x] = ri[x]; }
  }
  // Columns.
  for (let x = 0; x < N; x++) {
    for (let y = 0; y < N; y++) { rr[y] = re[y * N + x]; ri[y] = im[y * N + x]; }
    fftInPlace(rr, ri, N, cosT, sinT);
    for (let y = 0; y < N; y++) { re[y * N + x] = rr[y]; im[y * N + x] = ri[y]; }
  }
  // (−1)^(x+y) recentres the k = 0 texel at N/2 (fft-pure's convention).
  const sx = new Float64Array(N * N), sz = new Float64Array(N * N);
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const s = (x + y) & 1 ? -1 : 1;
    const i = y * N + x;
    sx[i] = re[i] * s;
    sz[i] = im[i] * s;
  }
  return { sx, sz, N, patchSize: L };
}

/** Mode-energy shares of a slope field (for the lattice statistic). */
function modeShares(sx: Float64Array, sz: Float64Array, N: number): { peak: number; top4: number } {
  // Spectrum G of the packed field sx + i·sz (the kernel's sign is irrelevant for energies).
  const re = new Float64Array(N * N), im = new Float64Array(N * N);
  for (let i = 0; i < N * N; i++) { re[i] = sx[i]; im[i] = sz[i]; }
  const cosT = new Float64Array(N / 2), sinT = new Float64Array(N / 2);
  for (let k = 0; k < N / 2; k++) { cosT[k] = Math.cos((Math.PI * 2 * k) / N); sinT[k] = Math.sin((Math.PI * 2 * k) / N); }
  const rr = new Float64Array(N), ri = new Float64Array(N);
  for (let y = 0; y < N; y++) { const b = y * N; for (let x = 0; x < N; x++) { rr[x] = re[b + x]; ri[x] = im[b + x]; } fftInPlace(rr, ri, N, cosT, sinT); for (let x = 0; x < N; x++) { re[b + x] = rr[x]; im[b + x] = ri[x]; } }
  for (let x = 0; x < N; x++) { for (let y = 0; y < N; y++) { rr[y] = re[y * N + x]; ri[y] = im[y * N + x]; } fftInPlace(rr, ri, N, cosT, sinT); for (let y = 0; y < N; y++) { re[y * N + x] = rr[y]; im[y * N + x] = ri[y]; } }
  // Energy per ±k PAIR (a real sinusoid is one pair): |G(k)|² + |G(−k)|² = 2(|X(k)|² + |Z(k)|²).
  // The four-sine map puts its whole variance into 4 pairs (top4 ≈ 1); a spectrum spreads it.
  const e: number[] = [];
  let total = 0;
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const i = y * N + x;
    const j = ((N - y) % N) * N + ((N - x) % N);
    if (j < i) continue; // each pair once (the self-paired DC/Nyquist texels counted once)
    const v = re[i] * re[i] + im[i] * im[i] + (j !== i ? re[j] * re[j] + im[j] * im[j] : 0);
    e.push(v); total += v;
  }
  if (total <= 0) return { peak: 0, top4: 0 };
  e.sort((a, b) => b - a);
  return { peak: e[0] / total, top4: (e[0] + (e[1] ?? 0) + (e[2] ?? 0) + (e[3] ?? 0)) / total };
}

/**
 * Bake the detail normal map. Returns the RGBA8 DataTexture (repeat wrapped,
 * trilinear + mips, linear colour space) and its statistics.
 */
export function bakeSpectrumNormalMap(config: SpectrumNormalMapConfig = {}): SpectrumNormalMap {
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const { sx, sz, N, patchSize } = spectrumSlopeField(config);
  let ms2 = 0;
  for (let i = 0; i < N * N; i++) ms2 += sx[i] * sx[i] + sz[i] * sz[i];
  const rmsRaw = Math.sqrt(ms2 / (N * N)) || 1e-9;
  const target = config.targetRmsSlope ?? 0.3;
  const g = target / rmsRaw;
  const data = new Uint8Array(N * N * 4);
  let msStored = 0;
  for (let i = 0; i < N * N; i++) {
    const nx = -sx[i] * g, nz = -sz[i] * g;
    msStored += nx * nx + nz * nz;
    const len = Math.sqrt(nx * nx + 1 + nz * nz);
    const o = i * 4;
    data[o] = Math.round(((nx / len) * 0.5 + 0.5) * 255);
    data[o + 1] = Math.round(((1 / len) * 0.5 + 0.5) * 255);
    data[o + 2] = Math.round(((nz / len) * 0.5 + 0.5) * 255);
    data[o + 3] = 255;
  }
  // Seam statistic on the stored bytes (the old map's regression test measured the same).
  let maxInterior = 0, maxWrap = 0;
  for (const c of [0, 2]) {
    for (let a = 0; a < N; a++) {
      for (let b = 0; b < N - 1; b++) {
        maxInterior = Math.max(maxInterior, Math.abs(data[((b + 1) * N + a) * 4 + c] - data[(b * N + a) * 4 + c]));
        maxInterior = Math.max(maxInterior, Math.abs(data[(a * N + b + 1) * 4 + c] - data[(a * N + b) * 4 + c]));
      }
      maxWrap = Math.max(maxWrap, Math.abs(data[(a) * 4 + c] - data[((N - 1) * N + a) * 4 + c]));
      maxWrap = Math.max(maxWrap, Math.abs(data[(a * N) * 4 + c] - data[(a * N + N - 1) * 4 + c]));
    }
  }
  const shares = !config.fullStats ? { peak: NaN, top4: NaN } : N <= 256 ? modeShares(sx, sz, N) : modeShares(downsample(sx, N), downsample(sz, N), N / 2);
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 4;
  tex.colorSpace = THREE.NoColorSpace;
  tex.name = `SpectrumWaterNormals_${N}_${patchSize}m`;
  tex.needsUpdate = true;
  const stats: SpectrumNormalStats = {
    resolution: N,
    patchSize,
    rmsSlope: Math.sqrt(msStored / (N * N)),
    meanSquareSlope: msStored / (N * N),
    peakModeShare: shares.peak,
    top4Share: shares.top4,
    maxWrapStep: maxWrap,
    maxInteriorStep: maxInterior,
    ms: (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0,
  };
  tex.userData.spectrumStats = stats;
  return { texture: tex, stats };
}

/** 2× box downsample of a periodic field (the mode statistic on a 512² bake). */
function downsample(f: Float64Array, N: number): Float64Array {
  const M = N / 2;
  const out = new Float64Array(M * M);
  for (let y = 0; y < M; y++) for (let x = 0; x < M; x++) {
    out[y * M + x] = 0.25 * (f[2 * y * N + 2 * x] + f[2 * y * N + 2 * x + 1] + f[(2 * y + 1) * N + 2 * x] + f[(2 * y + 1) * N + 2 * x + 1]);
  }
  return out;
}

/**
 * The same statistics for any RGBA8 normal map packed like the water maps —
 * the harness measures the OLD four-sine map with it (the lattice oracle).
 */
export function normalMapStats(data: Uint8Array, N: number): { rmsSlope: number; peakModeShare: number; top4Share: number } {
  const sx = new Float64Array(N * N), sz = new Float64Array(N * N);
  let ms = 0;
  for (let i = 0; i < N * N; i++) {
    const nx = data[i * 4] / 255 * 2 - 1, ny = data[i * 4 + 1] / 255 * 2 - 1, nz = data[i * 4 + 2] / 255 * 2 - 1;
    const y = Math.max(0.05, ny);
    sx[i] = -nx / y; sz[i] = -nz / y;
    ms += (nx / y) ** 2 + (nz / y) ** 2;
  }
  const s = modeShares(sx, sz, N);
  return { rmsSlope: Math.sqrt(ms / (N * N)), peakModeShare: s.peak, top4Share: s.top4 };
}
